import { firstValueFrom } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { TS1FormResponseJSON } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { NgxIndexedDBService } from "ngx-indexed-db";

export { isEts1SessionV2 } from "src/app/cbta-web/ets1/session/ets1-session-form.utils";

/** Infer admin form config version from assessment shape when `configVersion` was not persisted. */
export function inferFormConfigVersion(formDetails?: TS1FormResponseJSON | null): number {
	if (formDetails?.configVersion != null && !Number.isNaN(Number(formDetails.configVersion))) {
		return Number(formDetails.configVersion);
	}

	const configs = formDetails?.assessmentConfig ?? [];
	for (const cfg of configs) {
		for (const ob of cfg?.observations ?? []) {
			if ((ob as { candidateRole?: string }).candidateRole) {
				return 2;
			}
		}
	}

	return 1;
}

/** Apply resolved configVersion onto form details (immutable). */
export function withFormConfigVersion(
	formDetails: TS1FormResponseJSON,
	configVersion: number,
): TS1FormResponseJSON {
	return formDetails.configVersion === configVersion ? formDetails : { ...formDetails, configVersion };
}

/** Merge sparse draft/form payloads with a fuller FORM-store or API snapshot for config inference. */
export function mergeFormDetailsForConfigInference(
	formDetails: TS1FormResponseJSON,
	supplemental?: TS1FormResponseJSON | null,
): TS1FormResponseJSON {
	if (!supplemental) return formDetails;

	return {
		...formDetails,
		configVersion:
			formDetails.configVersion != null && !Number.isNaN(Number(formDetails.configVersion))
				? formDetails.configVersion
				: supplemental.configVersion,
		assessmentConfig: formDetails.assessmentConfig?.length
			? formDetails.assessmentConfig
			: supplemental.assessmentConfig,
	};
}

/** Prefer live assessmentConfig from API (includes admin conditional OB flags). */
export function mergeFreshAssessmentConfig(
	formDetails: TS1FormResponseJSON,
	fresh?: TS1FormResponseJSON | null,
): TS1FormResponseJSON {
	if (!fresh?.assessmentConfig?.length) {
		return formDetails;
	}

	const configVersion =
		fresh.configVersion != null && !Number.isNaN(Number(fresh.configVersion))
			? Number(fresh.configVersion)
			: formDetails.configVersion;

	if (formDetails.assessmentConfig === fresh.assessmentConfig && formDetails.configVersion === configVersion) {
		return formDetails;
	}

	return {
		...formDetails,
		...(configVersion != null ? { configVersion } : {}),
		assessmentConfig: fresh.assessmentConfig,
	};
}

/** Apply supplemental FORM/API payload onto sparse form details (configVersion + assessmentConfig). */
export function applySupplementalFormDetails(
	formDetails: TS1FormResponseJSON,
	supplemental?: TS1FormResponseJSON | null,
): TS1FormResponseJSON {
	if (!supplemental) {
		return withFormConfigVersion(formDetails, inferFormConfigVersion(formDetails));
	}

	const mergedForInfer = mergeFormDetailsForConfigInference(formDetails, supplemental);
	const configVersion = inferFormConfigVersion(mergedForInfer);
	const assessmentConfig = formDetails.assessmentConfig?.length
		? formDetails.assessmentConfig
		: supplemental.assessmentConfig;

	if (
		formDetails.configVersion === configVersion &&
		formDetails.assessmentConfig === assessmentConfig
	) {
		return formDetails;
	}

	return {
		...formDetails,
		configVersion,
		...(assessmentConfig?.length ? { assessmentConfig } : {}),
	};
}

/** Resolve configVersion from field, observation shape, or default — for Step 2 UI gating. */
export function resolveFormDetailsForRating(formDetails?: TS1FormResponseJSON | null): TS1FormResponseJSON | undefined {
	if (!formDetails) return undefined;
	return withFormConfigVersion(formDetails, inferFormConfigVersion(formDetails));
}

export function isFormConfigV2Details(formDetails?: TS1FormResponseJSON | null): boolean {
	return inferFormConfigVersion(formDetails) === 2;
}

/**
 * Ensure `formDetails.configVersion` is set using local cache, live API, or observation-shape inference.
 * Fixes stale IndexedDB FORM / draft payloads that omit configVersion until a hard reload.
 */
export async function hydrateFormDetailsConfigVersion(
	formDetails: TS1FormResponseJSON,
	formId: string | undefined,
	eTrainingDb: NgxIndexedDBService,
	httpService?: HttpService,
): Promise<TS1FormResponseJSON> {
	if (formId && navigator.onLine && httpService) {
		try {
			const raw = await firstValueFrom(httpService.getForm(formId));
			const fresh = (raw as { data?: TS1FormResponseJSON })?.data ?? (raw as TS1FormResponseJSON);
			if (fresh) {
				const hydrated = mergeFreshAssessmentConfig(formDetails, fresh);
				try {
					await firstValueFrom(
						eTrainingDb.update(CONSTANT.INDEXEDDB.FORM, {
							id: formId,
							data: hydrated,
							stringify: JSON.stringify(hydrated),
						}),
					);
				} catch {
					// non-blocking cache refresh
				}
				return hydrated;
			}
		} catch {
			// ignore — fall through to cache / inference
		}
	}

	const hasConfigVersion =
		formDetails?.configVersion != null && !Number.isNaN(Number(formDetails.configVersion));
	const hasAssessmentConfig = (formDetails?.assessmentConfig?.length ?? 0) > 0;
	if (hasConfigVersion && hasAssessmentConfig) {
		return formDetails;
	}

	let supplemental: TS1FormResponseJSON | undefined;

	if (formId) {
		try {
			const cached = await firstValueFrom(
				eTrainingDb.getByKey<{ data?: TS1FormResponseJSON }>(CONSTANT.INDEXEDDB.FORM, formId),
			);
			supplemental = cached?.data;
			const cachedVersion = supplemental?.configVersion;
			if (cachedVersion != null && !Number.isNaN(Number(cachedVersion))) {
				return applySupplementalFormDetails(formDetails, supplemental);
			}
		} catch {
			// ignore — fall through to inference
		}
	}

	return applySupplementalFormDetails(formDetails, supplemental);
}

/** Merge admin-configured session remark defaults from a fuller form payload. */
export function mergeFormSessionDetailsRemarks(
	formDetails: TS1FormResponseJSON,
	supplemental?: TS1FormResponseJSON | null,
): TS1FormResponseJSON {
	if (!supplemental?.sessionDetails?.length) return formDetails;

	const remarksByCode = new Map(
		supplemental.sessionDetails
			.filter((s) => s.selectionRemarks?.trim())
			.map((s) => [s.code, s.selectionRemarks!.trim()]),
	);
	if (!remarksByCode.size) return formDetails;

	const mergedSessionDetails = (formDetails.sessionDetails ?? []).map((s) => {
		const remark = remarksByCode.get(s.code);
		if (!remark || s.selectionRemarks?.trim()) return s;
		return { ...s, selectionRemarks: remark };
	});

	const unchanged =
		mergedSessionDetails.length === (formDetails.sessionDetails ?? []).length &&
		mergedSessionDetails.every((s, i) => s === (formDetails.sessionDetails ?? [])[i]);
	if (unchanged) return formDetails;

	return { ...formDetails, sessionDetails: mergedSessionDetails };
}

/**
 * Ensure `formDetails.sessionDetails[].selectionRemarks` are present when the FORM store
 * was populated from list endpoints that omit remark defaults.
 */
export async function hydrateFormSessionDetailsRemarks(
	formDetails: TS1FormResponseJSON,
	formId: string | undefined,
	httpService?: HttpService,
): Promise<TS1FormResponseJSON> {
	const hasRemarks = (formDetails.sessionDetails ?? []).some((s) => s.selectionRemarks?.trim());
	if (hasRemarks || !formId || !navigator.onLine || !httpService) {
		return formDetails;
	}

	try {
		const raw = await firstValueFrom(httpService.getForm(formId));
		const fresh = (raw as { data?: TS1FormResponseJSON })?.data ?? (raw as TS1FormResponseJSON);
		return mergeFormSessionDetailsRemarks(formDetails, fresh);
	} catch {
		return formDetails;
	}
}

/** Merge admin Reminder Notes config from a fuller form payload onto sparse draft/cache formDetails. */
export function mergeFormReminderNotes(
	formDetails: TS1FormResponseJSON,
	supplemental?: TS1FormResponseJSON | null,
): TS1FormResponseJSON {
	if (!supplemental) return formDetails;

	const enabled =
		supplemental.reminderNotesEnabled ??
		(supplemental as { reminder_notes_enabled?: boolean }).reminder_notes_enabled;
	if (enabled === undefined) return formDetails;

	const rawTemplate =
		supplemental.reminderNotesTemplate ??
		(supplemental as { reminder_notes_template?: string | null }).reminder_notes_template ??
		"";
	const reminderNotesEnabled = !!enabled;
	const reminderNotesTemplate = reminderNotesEnabled ? String(rawTemplate ?? "") : undefined;

	if (
		formDetails.reminderNotesEnabled === reminderNotesEnabled &&
		(formDetails.reminderNotesTemplate ?? "") === (reminderNotesTemplate ?? "")
	) {
		return formDetails;
	}

	return {
		...formDetails,
		reminderNotesEnabled,
		reminderNotesTemplate,
	};
}

/**
 * Ensure `formDetails.reminderNotesEnabled` / template reflect the live admin form config.
 * Fixes stale IndexedDB FORM / draft snapshots that omitted or incorrectly defaulted these fields.
 */
export async function hydrateFormReminderNotes(
	formDetails: TS1FormResponseJSON,
	formId: string | undefined,
	httpService?: HttpService,
	eTrainingDb?: NgxIndexedDBService,
): Promise<TS1FormResponseJSON> {
	if (!formId) return formDetails;

	let supplemental: TS1FormResponseJSON | undefined;

	if (eTrainingDb) {
		try {
			const cached = await firstValueFrom(
				eTrainingDb.getByKey<{ data?: TS1FormResponseJSON }>(CONSTANT.INDEXEDDB.FORM, formId),
			);
			if (cached?.data && cached.data.reminderNotesEnabled !== undefined) {
				supplemental = cached.data;
			}
		} catch {
			// ignore — fall through to API
		}
	}

	if (navigator.onLine && httpService) {
		try {
			const raw = await firstValueFrom(httpService.getForm(formId));
			const fresh = (raw as { data?: TS1FormResponseJSON })?.data ?? (raw as TS1FormResponseJSON);
			if (fresh?.reminderNotesEnabled !== undefined) {
				supplemental = supplemental ? { ...supplemental, ...fresh } : fresh;
			}
		} catch {
			// keep cache supplemental if any
		}
	}

	return mergeFormReminderNotes(formDetails, supplemental);
}
